import { isAccumulatingMetric, METRIC_GOOD_DIRECTION, metricLabelPastVerb, metricLabelVerb, movedFromUsual, } from "../signals.js";
/** What one reading of this metric covers, for describing a run of them. */
/** What one reading of this kind covers, so a run can be counted in it. */
const PERIOD_NOUN = {
    "wearable.sleep": "night",
    "wearable.hrv": "night",
    "wearable.rhr": "night",
    "wearable.spo2": "night",
    "wearable.respiration": "night",
    "wearable.activity": "day",
    "nutrition.sodium_mg": "day",
    "nutrition.protein_g": "day",
    "hydration.ml": "day",
};
const NUMBER_WORD = [
    "",
    "",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
];
/**
 * The run, as a member would say it: "three nights in a row". Null below two,
 * because one reading is an off day and calling it a pattern is the same
 * overstatement in the other direction.
 *
 * Not "running", which was the word the read block next door already used and
 * the reason this first shipped with it. In a product that counts steps and
 * talks about walking, "four days running" is four days of running, and the
 * one place a member is told what we noticed is the last place to hand them a
 * second reading of it. "In a row" is also simply the plainer phrase.
 */
export function observationStreakPhrase(b) {
    // A run counts finished days, so it cannot describe a part-day direction.
    if (b.partialToday != null)
        return null;
    return streakPhrase(b.kind, b.streak ?? 0);
}
/** The same phrase for a run already counted elsewhere. */
export function streakPhrase(kind, days) {
    if (days < 2)
        return null;
    const count = NUMBER_WORD[days] ?? String(days);
    return `${count} ${PERIOD_NOUN[kind] ?? "reading"}s in a row`;
}
const round = (n) => Math.round(n * 10) / 10;
const grouped = (n) => Math.round(n).toLocaleString("en-GB");
/** Which side of usual, or null when level. A part-day total can only argue "above". */
export function directionFromBaseline(b, tolerance = LEVEL_TOLERANCE) {
    if (!Number.isFinite(b.latest) || !Number.isFinite(b.average) || b.average === 0)
        return null;
    return sideOfUsual(b, tolerance);
}
/** Today's total has already cleared the usual, so nothing is behind. */
function aheadOnPartialDay(b) {
    if (b.partialToday == null || METRIC_GOOD_DIRECTION[b.kind] !== "above")
        return false;
    return b.partialToday >= b.average;
}
function sideOfUsual(b, tolerance = LEVEL_TOLERANCE) {
    if (b.partialToday != null)
        return aheadOnPartialDay(b) ? "above" : null;
    // A day on the usual is not a day above it.
    if (!movedFromUsual(b.kind, b.latest, b.average, { spread: b.spread, band: tolerance })) {
        return null;
    }
    return b.latest < b.average ? "below" : "above";
}
// Level means level, not "close". A wider band silences a real move on a
// low-variance metric: skin temperature 0.4 degrees off usual is 1 percent.
const LEVEL_TOLERANCE = 0.005;
/**
 * The observation, written from the member's own numbers rather than derived by
 * a model. This is the half that makes a step feel noticed, and the half a
 * generator most often gets subtly wrong, so it is computed rather than asked
 * for: a blind read caught the generator reporting 5.1 hours of sleep to a
 * member who had slept 8.2.
 *
 * Clinical metrics render as a direction only, matching how baselines are
 * spoken everywhere else: a member should not read their blood pressure back
 * as a number from a push notification.
 */
/** What the situation block says when nothing is off the member's usual. One spelling, so a guard can match it. */
export const NOTHING_NOTABLE_VS_USUAL = "(nothing notable vs this user's usual yet)";
export function observationFromBaseline(b) {
    if (!Number.isFinite(b.latest) || !Number.isFinite(b.average) || b.average === 0)
        return null;
    const direction = sideOfUsual(b);
    const verb = metricLabelVerb(b.kind);
    // A part-day total is behind every morning by construction, so comparing it
    // to a whole-day usual tells the member they are short when nothing is wrong.
    if (direction === null && b.partialToday != null)
        return null;
    if (b.clinical && direction === null)
        return null;
    // "sitting", not "running": the reading is where it has settled, and this
    // app counts steps, so "running below your usual" reads as the exercise.
    if (b.clinical)
        return `Your ${b.label} ${verb} sitting ${direction} your usual`;
    const isWholeCount = b.unit === "steps" || b.unit === "ml";
    const reading = b.partialToday ?? b.latest;
    const latest = isWholeCount ? grouped(reading) : round(reading);
    const average = isWholeCount ? grouped(b.average) : round(b.average);
    // The unit is what makes the number mean anything, so it is dropped only when
    // the label already carries it. Suppressing it for every counted metric read
    // "your hydration is at 355 against a usual of 267", which names no unit at
    // all, while "your daily steps are at 6,327 steps" merely repeats one.
    const labelNamesUnit = b.label.toLowerCase().includes(b.unit.toLowerCase());
    // A ratio or percent unit joins the number ("44/100"); a named unit takes a space.
    const unit = labelNamesUnit ? "" : /^[/%]/.test(b.unit) ? b.unit : ` ${b.unit}`;
    const when = readingPeriod(b);
    return `Your ${b.label} ${when.verb ?? verb} at ${latest}${unit}${when.suffix} against a usual of ${average}${unit}`;
}
/**
 * When the number being printed was measured, in the tense and the words a
 * member reads it in.
 *
 * The tense is decided here rather than by the caller because the caller kept
 * getting it wrong in one direction only. An accumulating metric's `latest` is
 * the last FINISHED day, which is yesterday or older by construction, and the
 * sentence shipped as "Your daily steps are at 8,356": a member's step total
 * from yesterday, in the present tense, on a card stamped three hours ago. No
 * field a caller can forget to set will bring the present tense back.
 */
function readingPeriod(b) {
    if (b.partialToday != null)
        return { suffix: " so far today" };
    if (!isAccumulatingMetric(b.kind))
        return { suffix: "" };
    const days = b.latestDay && b.today ? wholeDaysBetween(b.latestDay, b.today) : null;
    // A "finished" day that is in fact today means the caller labelled a running
    // total as complete. Say so in the present tense rather than calling the
    // member's afternoon a finished day.
    if (days !== null && days <= 0)
        return { suffix: " so far today" };
    if (days === null)
        return { verb: metricLabelPastVerb(b.kind), suffix: " on your last full day" };
    const when = days === 1 ? "yesterday" : `${days} days ago`;
    return { verb: metricLabelPastVerb(b.kind), suffix: ` ${when}` };
}
const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Whole days from `from` to `to`, both `YYYY-MM-DD`. Null if either is not. */
function wholeDaysBetween(from, to) {
    const a = DAY_KEY.exec(from);
    const b = DAY_KEY.exec(to);
    if (!a || !b)
        return null;
    const ms = Date.UTC(+b[1], +b[2] - 1, +b[3]) -
        Date.UTC(+a[1], +a[2] - 1, +a[3]);
    return Math.round(ms / 86_400_000);
}
/**
 * How a rationale states what moved, in different words from the observation.
 *
 * It names the metric rather than opening on "That". The pronoun read fine on
 * the plan card, where the observation sits directly above it, and nowhere
 * else: the attention card renders the rationale alone, so a member met "that
 * is ahead of where you usually land" under a headline reading "4,924 steps so
 * far today" when the reading in question was their heart rate variability. A
 * sentence that only works next to another sentence is not safe to hand to a
 * surface that will not show the other one.
 *
 * Null for a clinical metric, whose observation is already only a direction:
 * restating "running above your usual" as "ahead of where you usually land"
 * says the same thing twice, and the mechanism is the honest thing to say next.
 *
 * Warmth follows the metric's good direction, not the raw one. Reading
 * direction alone congratulated a member on a stress reading above their usual,
 * the same inversion a blind read caught the generator making. A metric with no
 * good side stays level in both.
 */
export function observationRestatement(b) {
    const base = restatementWithoutRun(b);
    if (!base)
        return null;
    const run = observationStreakPhrase(b);
    // The run is the reason today is the day. Without it the sentence is about
    // last night and the card is answering "why this" a second time.
    return run ? `${base}, ${run}` : base;
}
function restatementWithoutRun(b) {
    if (b.clinical)
        return null;
    const direction = sideOfUsual(b);
    if (direction === null)
        return null;
    const subject = `Your ${b.label} ${metricLabelVerb(b.kind)}`;
    const good = METRIC_GOOD_DIRECTION[b.kind];
    if (!good) {
        return direction === "below"
            ? `${subject} under your usual reading`
            : `${subject} over your usual reading`;
    }
    if (direction === good) {
        return direction === "below"
            ? `${subject} lower than you usually run, which is the good direction`
            : `${subject} ahead of where you usually land`;
    }
    return direction === "below"
        ? `${subject} below where you usually land`
        : `${subject} higher than you usually run`;
}
//# sourceMappingURL=observation.js.map